import {Attributes} from 'platform/components';
import {Heading, HStack, VStack, Text} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {useEffect, useState} from 'react';

import {GetVehicleApiResponseV2} from '@dms/api/shared';
import {useGetLatestAiVehicleServiceIntervalsQuery} from '@dms/api/vehicle';
import {useAnnualMileageQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {parseDate, RequiredTestIdProps, suffixTestId, Nullish, EMPTY_PLACEHOLDER} from 'shared/web';

import {VehicleServiceIntervalFluidAttributes} from './VehicleServiceIntervalFluidAttributes';
import {VehicleServiceIntervalReplacementAttributes} from './VehicleServiceIntervalReplacementAttributes';

const AI_SERVICE_INTERVALS_POLLING_INTERVAL_MS = 10_000;

interface VehicleServiceIntervalsAIAttributesProps extends RequiredTestIdProps {
  vehicleData: GetVehicleApiResponseV2 | Nullish;
  expectedAiIntervalId?: string | null;
  onExpectedAiIntervalCompleted?: () => void;
}

export function VehicleServiceIntervalsAIAttributes(
  props: VehicleServiceIntervalsAIAttributesProps
) {
  const formatDateTime = useDateTimeFormatter();

  const annualMileageQuery = useAnnualMileageQuery(
    {vehicleId: props.vehicleData?.id || ''},
    {skip: !props.vehicleData?.id}
  );

  const [aiPollingInterval, setAiPollingInterval] = useState(0);
  const {data: latestAiServiceIntervals} = useGetLatestAiVehicleServiceIntervalsQuery(
    {vehicleId: props.vehicleData?.id || ''},
    {
      skip: !props.vehicleData?.id,
      pollingInterval: aiPollingInterval,
    }
  );
  useEffect(() => {
    const expectedId = props.expectedAiIntervalId ?? null;
    const latest = latestAiServiceIntervals;
    const isExpectedAndCompleted =
      expectedId && latest && latest.id === expectedId && latest.status === 'completed';
    if (isExpectedAndCompleted) {
      props.onExpectedAiIntervalCompleted?.();
    }
    const shouldPoll =
      expectedId && (!latest || latest.id !== expectedId || latest.status !== 'completed');
    setAiPollingInterval(shouldPoll ? AI_SERVICE_INTERVALS_POLLING_INTERVAL_MS : 0);
  }, [props, latestAiServiceIntervals]);

  const serviceInspection = props.vehicleData?.serviceIntervals?.serviceInspection;
  const engineOil = props.vehicleData?.serviceIntervals?.engineOil;
  const gearboxOil = props.vehicleData?.serviceIntervals?.gearboxOil;
  const fourByFourOilDistributor = props.vehicleData?.serviceIntervals?.fourByFourOilDistributor;
  const timingBelt = props.vehicleData?.serviceIntervals?.timingBelt;
  const brakeFluid = props.vehicleData?.serviceIntervals?.brakeFluid;
  const coolantFluid = props.vehicleData?.serviceIntervals?.coolantFluid;

  return (
    <VStack spacing={4}>
      <Heading size={4}>{i18n.t('entity.vehicle.labels.serviceInspection')}</Heading>
      <Attributes
        rows={[
          {
            label: i18n.t('entity.vehicle.labels.dateOfLastInspection'),
            content: (
              <HStack justify="space-between">
                <Text size="xSmall">
                  {serviceInspection?.lastServiceInspectionDate
                    ? formatDateTime(
                        'dateShort',
                        parseDate(serviceInspection.lastServiceInspectionDate)
                      )
                    : EMPTY_PLACEHOLDER}
                </Text>
                <Text size="xSmall">{EMPTY_PLACEHOLDER}</Text>
              </HStack>
            ),
          },
          {
            label: i18n.t('entity.vehicle.labels.replacementAfterMonths'),
            content: (
              <HStack justify="space-between">
                <Text size="xSmall">
                  {serviceInspection?.replacementAfterMonths ?? EMPTY_PLACEHOLDER}
                </Text>
                <Text size="xSmall">
                  {latestAiServiceIntervals?.values?.serviceInspection?.afterMonths ??
                    EMPTY_PLACEHOLDER}
                </Text>
              </HStack>
            ),
          },
          {
            label: i18n.t('entity.vehicle.labels.dateOfNextServiceInspection'),
            content: (
              <HStack justify="space-between">
                <Text size="xSmall">
                  {serviceInspection?.nextServiceInspectionDate
                    ? formatDateTime(
                        'dateShort',
                        parseDate(serviceInspection?.nextServiceInspectionDate)
                      )
                    : EMPTY_PLACEHOLDER}
                </Text>
                <Text size="xSmall">
                  {latestAiServiceIntervals?.values?.serviceInspection?.nextDate
                    ? formatDateTime(
                        'dateShort',
                        parseDate(latestAiServiceIntervals?.values?.serviceInspection?.nextDate)
                      )
                    : EMPTY_PLACEHOLDER}
                </Text>
              </HStack>
            ),
          },
          {
            label: `${i18n.t('entity.vehicle.labels.lastServiceInspection')} (${i18n.t('general.metric.km')})`,
            content: (
              <HStack justify="space-between">
                <Text size="xSmall">
                  {serviceInspection?.lastServiceInspectionMileage ?? EMPTY_PLACEHOLDER}
                </Text>
                <Text size="xSmall">{EMPTY_PLACEHOLDER}</Text>
              </HStack>
            ),
          },
          {
            label: `${i18n.t('entity.vehicle.labels.replacementAfterMileage')} (${i18n.t('general.metric.km')})`,
            content: (
              <HStack justify="space-between">
                <Text size="xSmall">
                  {serviceInspection?.replacementAfterMileage ?? EMPTY_PLACEHOLDER}
                </Text>
                <Text size="xSmall">
                  {latestAiServiceIntervals?.values?.serviceInspection?.afterMileage ??
                    EMPTY_PLACEHOLDER}
                </Text>
              </HStack>
            ),
          },
          {
            label: `${i18n.t('entity.vehicle.labels.nextServiceInspection')} (${i18n.t('general.metric.km')})`,
            content: (
              <HStack justify="space-between">
                <Text size="xSmall">
                  {serviceInspection?.nextServiceInspectionMileage ?? EMPTY_PLACEHOLDER}
                </Text>
                <Text size="xSmall">
                  {latestAiServiceIntervals?.values?.serviceInspection?.nextMileage ??
                    EMPTY_PLACEHOLDER}
                </Text>
              </HStack>
            ),
          },
        ]}
        data-testid={suffixTestId('serviceInspection', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.engineOil')}</Heading>
      <VehicleServiceIntervalReplacementAttributes
        part={engineOil}
        aiPart={latestAiServiceIntervals?.values?.engineOil}
        data-testid={suffixTestId('engineOil', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.gearboxOil')}</Heading>
      <VehicleServiceIntervalReplacementAttributes
        part={gearboxOil}
        aiPart={latestAiServiceIntervals?.values?.gearboxOil}
        data-testid={suffixTestId('gearboxOil', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.fourByFourOilDistributor')}</Heading>
      <VehicleServiceIntervalReplacementAttributes
        part={fourByFourOilDistributor}
        aiPart={latestAiServiceIntervals?.values?.fourByFourOilDistributor}
        data-testid={suffixTestId('fourByFourOilDistributor', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.timingBelt')}</Heading>
      <VehicleServiceIntervalReplacementAttributes
        part={timingBelt}
        aiPart={latestAiServiceIntervals?.values?.timingBelt}
        data-testid={suffixTestId('timingBelt', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.brakeFluid')}</Heading>
      <VehicleServiceIntervalFluidAttributes
        part={brakeFluid}
        aiPart={latestAiServiceIntervals?.values?.brakeFluid}
        data-testid={suffixTestId('brakeFluid', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.coolantFluid')}</Heading>
      <VehicleServiceIntervalFluidAttributes
        part={coolantFluid}
        aiPart={latestAiServiceIntervals?.values?.coolantFluid}
        data-testid={suffixTestId('coolantFluid', props)}
      />
      <Heading size={4}>{i18n.t('entity.vehicle.labels.annualMileage')}</Heading>

      <Attributes
        rows={[
          {
            label: `${i18n.t('entity.vehicle.labels.serviceVisits')} (${i18n.t('general.metric.km')})`,

            content: (
              <HStack justify="space-between">
                <Text size="xSmall">
                  {annualMileageQuery.data?.annualMileage ?? EMPTY_PLACEHOLDER}
                </Text>
                <Text size="xSmall">
                  {latestAiServiceIntervals?.values?.annualMileage?.calculatedFromServiceVisits ??
                    EMPTY_PLACEHOLDER}
                </Text>
              </HStack>
            ),
          },
        ]}
        data-testid={suffixTestId('annualMileage', props)}
      />
    </VStack>
  );
}
